/**
 * Backing PQI Dataverse Service — completely independent. Do NOT import existing PQI service or saveSectionData.
 * Saves to Dataverse table: cr3ea_prod_pqi_bakeries.
 * Handles recordsToSave array, offline queue persistence, sync when online, attachment upload after record save.
 */

const ENVIRONMENT_URL = "https://orgea61b289.crm8.dynamics.com";
const API_VERSION = "9.2";
const BAKERIES_TABLE = "cr3ea_prod_pqi_bakeries";
const BACKING_PQI_QUEUE_KEY = "backingPQI_offlineQueue";
const BACKING_PQI_SYNCED_IDS_KEY = "backingPQI_syncedIds";

function getShift() {
  if (typeof sessionStorage === "undefined") return "shift 1";
  return sessionStorage.getItem("shiftValue") || "shift 1";
}

function formatTitleDate() {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const year = d.getFullYear();
  return `${month}-${day}-${year}`;
}

/**
 * Build a single record for cr3ea_prod_pqi_bakeries.
 * Defect fields (cr3ea_defectcategory, cr3ea_defect, cr3ea_majordefect, cr3ea_remarks) are always included so backend receives them.
 */
function buildRecord(params) {
  return {
    cr3ea_qualitytourid: params.qualityTourId ?? null,
    cr3ea_title: params.title ?? "PQI_" + formatTitleDate(),
    cr3ea_cycle: params.cycle ?? null,
    cr3ea_shift: params.shift ?? getShift(),
    cr3ea_observedby: params.observedBy ?? null,
    cr3ea_productname: params.productName ?? null,
    cr3ea_batchno: params.batchNo ?? null,
    cr3ea_lineno: params.lineNo ?? null,
    cr3ea_executivename: params.executiveName ?? null,
    cr3ea_packaged: params.packaged ?? null,
    cr3ea_expiry: params.expiry ?? null,
    cr3ea_standardweight: params.standardWeight ?? null,
    cr3ea_netweight: params.netWeight ?? null,
    cr3ea_category: params.category ?? null,
    cr3ea_itemno: params.itemNo ?? 1,
    cr3ea_status: params.status ?? null,
    cr3ea_defectcategory: params.defectCategory ?? null,
    cr3ea_defect: params.defect ?? null,
    cr3ea_majordefect: params.majorDefect ?? null,
    cr3ea_remarks: params.remarks ?? null,
  };
}

/**
 * Build recordsToSave array from header + rows for one set.
 * categoryLabel = tab name (Creates, Packs, Product, Net Weight).
 */
export function buildRecordsToSave(plantTourId, userName, categoryLabel, cycleNo, header, rows) {
  const recordsToSave = [];
  const shift = getShift();
  const title = "PQI_" + formatTitleDate();
  const cycleStr = `Cycle-${cycleNo}`;

  (rows || []).forEach((row, idx) => {
    const record = buildRecord({
      qualityTourId: plantTourId,
      title,
      cycle: cycleStr,
      shift,
      observedBy: userName,
      productName: header?.product ?? null,
      batchNo: header?.batchNo ?? null,
      lineNo: header?.lineNo ?? null,
      executiveName: header?.executiveName ?? null,
      packaged: header?.packaged ?? null,
      expiry: header?.expiry ?? null,
      standardWeight: header?.standardWeight ?? null,
      netWeight: row.netWeight ?? null,
      category: categoryLabel,
      itemNo: row.itemNum ?? idx + 1,
      status: row.status ?? null,
      defectCategory: row.defectCategory ?? null,
      defect: row.defect ?? null,
      majorDefect: row.majorDefect ?? null,
      remarks: row.remarks ?? row.majorDefect ?? null,
    });
    recordsToSave.push(record);
  });

  return recordsToSave;
}

/**
 * Persist offline queue to localStorage (used by Redux middleware or after enqueueOffline).
 */
export function persistOfflineQueue(queue) {
  try {
    localStorage.setItem(BACKING_PQI_QUEUE_KEY, JSON.stringify(queue || []));
  } catch (e) {
    console.warn("backingPQIDataverseService: Failed to persist offline queue", e);
  }
}

/**
 * Load offline queue from localStorage.
 */
export function loadOfflineQueue() {
  try {
    const raw = localStorage.getItem(BACKING_PQI_QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

/**
 * Mark a queue item as synced (by id) to prevent duplicate submission.
 */
function markSyncedId(id) {
  try {
    const raw = localStorage.getItem(BACKING_PQI_SYNCED_IDS_KEY);
    const set = new Set(raw ? JSON.parse(raw) : []);
    set.add(id);
    localStorage.setItem(BACKING_PQI_SYNCED_IDS_KEY, JSON.stringify([...set]));
  } catch (e) {
    console.warn("backingPQIDataverseService: Failed to mark synced id", e);
  }
}

function isSyncedId(id) {
  try {
    const raw = localStorage.getItem(BACKING_PQI_SYNCED_IDS_KEY);
    const set = new Set(raw ? JSON.parse(raw) : []);
    return set.has(id);
  } catch (e) {
    return false;
  }
}

function getBearerToken() {
  if (typeof sessionStorage === "undefined") return null;
  const t = sessionStorage.getItem("accessToken") || sessionStorage.getItem("dvAccessToken") || sessionStorage.getItem("token");
  if (t && t.trim()) return t.trim();
  const stored = localStorage.getItem("access_token");
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      if (parsed && parsed.token) return parsed.token;
    } catch (_) {}
  }
  return null;
}

function getHeaders(token) {
  return {
    Accept: "application/json",
    "Content-Type": "application/json; charset=utf-8",
    "OData-MaxVersion": "4.0",
    "OData-Version": "4.0",
    Authorization: `Bearer ${token}`,
  };
}

/**
 * Submit recordsToSave to Dataverse (cr3ea_prod_pqi_bakeries). Returns { success, results, errors }.
 */
export async function saveRecordsToDataverse(recordsToSave, accessToken) {
  const token = accessToken || getBearerToken();
  if (!token) {
    return { success: false, results: [], errors: ["No access token"] };
  }
  const url = `${ENVIRONMENT_URL}/api/data/v${API_VERSION}/${BAKERIES_TABLE}`;
  const headers = { ...getHeaders(token), Prefer: "return=representation" };
  const results = [];
  const errors = [];

  for (let i = 0; i < (recordsToSave || []).length; i++) {
    const record = recordsToSave[i];
    if (!record.cr3ea_cycle || !record.cr3ea_category || !record.cr3ea_qualitytourid) {
      errors.push(`Record ${i + 1}: missing required fields`);
      results.push(null);
      continue;
    }
    try {
      const res = await fetch(url, { method: "POST", headers, body: JSON.stringify(record) });
      if (!res.ok) {
        const text = await res.text();
        errors.push(`Record ${i + 1}: ${res.status} ${text}`);
        results.push(null);
      } else {
        results.push(await res.json());
      }
    } catch (e) {
      const msg = e && e.message ? e.message : String(e);
      errors.push(`Record ${i + 1}: ${msg}`);
      results.push(null);
    }
  }

  return { success: errors.length === 0, results, errors };
}

/**
 * Upload attachment for a saved record (e.g. to annotation or file column).
 */
export async function uploadAttachment(recordId, file, fileName, accessToken) {
  const token = accessToken || getBearerToken();
  if (!token) return { success: false, error: "No access token" };
  const name = fileName || (file && file.name) || "attachment";
  const formData = new FormData();
  formData.append("file", file, name);
  const url = `${ENVIRONMENT_URL}/api/data/v${API_VERSION}/${BAKERIES_TABLE}(${recordId})/cr3ea_attachment`;
  const headers = { Authorization: `Bearer ${token}`, "OData-MaxVersion": "4.0", "OData-Version": "4.0" };
  try {
    const res = await fetch(url, { method: "POST", headers, body: formData });
    if (!res.ok) {
      const text = await res.text();
      return { success: false, error: `${res.status} ${text}` };
    }
    return { success: true, data: await res.json().catch(() => ({})) };
  } catch (e) {
    return { success: false, error: e && e.message ? e.message : String(e) };
  }
}

/**
 * Sync all pending offline queue items to Dataverse. Call when app comes online.
 * Dispatches setSyncStatus; caller should pass dispatch and removeFromOfflineQueue, persistOfflineQueue.
 * Returns { synced: number, failed: number, errors: string[] }.
 */
export async function syncOfflineQueue(accessToken, queue, dispatch, removeFromOfflineQueueAction, setSyncStatusAction) {
  if (!queue || queue.length === 0) return { synced: 0, failed: 0, errors: [] };
  if (dispatch && setSyncStatusAction) dispatch(setSyncStatusAction("syncing"));

  const errors = [];
  let synced = 0;
  let failed = 0;

  for (const item of queue) {
    if (isSyncedId(item.id)) {
      if (dispatch && removeFromOfflineQueueAction) dispatch(removeFromOfflineQueueAction(item.id));
      synced++;
      continue;
    }
    const { success, errors: recordErrors } = await saveRecordsToDataverse(item.records, accessToken);
    if (success) {
      markSyncedId(item.id);
      if (dispatch && removeFromOfflineQueueAction) dispatch(removeFromOfflineQueueAction(item.id));
      synced++;
    } else {
      failed++;
      errors.push(...(recordErrors || []));
    }
  }

  persistOfflineQueue(queue.filter((q) => !isSyncedId(q.id)));
  if (dispatch && setSyncStatusAction) dispatch(setSyncStatusAction(failed > 0 ? "failed" : "success"));
  return { synced, failed, errors };
}
